// src/components/product/ImageUploader.js
import React, { useRef, useEffect, useState } from "react";
import { Upload, ChevronLeft, ChevronRight, Star, X } from "lucide-react";

export function ImageUploader({
  images,
  previewURLs,
  coverIndex,
  onAddImages,
  onRemoveImage,
  onMoveImage,
  onSetCover,
  maxImages = 3,
  uploading = false,
  uploadProgress = { done: 0, total: 0 },
  error = null,
  labels = {}
}) {
  const fileInputRef = useRef(null);
  const dzRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) {
      onAddImages(files);
    }
  };

  useEffect(() => {
    const el = dzRef.current;
    if (!el) return;

    ["dragenter", "dragover", "dragleave", "drop"].forEach((evt) =>
      el.addEventListener(evt, handleDrag)
    );
    el.addEventListener("drop", handleDrop);

    return () => {
      ["dragenter", "dragover", "dragleave", "drop"].forEach((evt) =>
        el.removeEventListener(evt, handleDrag)
      );
      el.removeEventListener("drop", handleDrop);
    };
  }, []);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  if (previewURLs.length === 0) {
    return (
      <>
        <div
          ref={dzRef}
          className={`border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition cursor-pointer ${
            dragActive
              ? "border-green-500 bg-green-50 dark:bg-green-900/20"
              : "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-neutral-900/30 hover:border-green-400 dark:hover:border-green-600"
          }`}
        >
          <Upload className="w-8 h-8 text-gray-400 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {labels.addUpTo || "Add up to"} {maxImages} {labels.photos || "photos"}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            {labels.dragDrop || "Drag and drop or tap to upload"}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => onAddImages(Array.from(e.target.files || []))}
            disabled={uploading}
            className="hidden"
          />
          <button
            type="button"
            onClick={handleFileSelect}
            disabled={uploading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition disabled:opacity-50"
          >
            {labels.selectPhotos || "Select Photos"}
          </button>
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {previewURLs.map((url, i) => (
          <div key={i} className="relative group">
            <img
              src={url}
              alt={`Photo ${i + 1}`}
              className={`w-full aspect-square object-cover rounded-lg ${
                i === coverIndex
                  ? "ring-2 ring-green-600"
                  : "ring-1 ring-gray-200 dark:ring-gray-700"
              }`}
            />
            {i === coverIndex && (
              <span className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                {labels.cover || "Cover"}
              </span>
            )}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition rounded-lg flex items-center justify-center gap-2">
              {i > 0 && (
                <button
                  type="button"
                  onClick={() => onMoveImage(i, i - 1)}
                  className="p-2 bg-white rounded-full hover:bg-gray-100"
                  title={labels.moveLeft || "Move left"}
                >
                  <ChevronLeft className="w-4 h-4 text-gray-900" />
                </button>
              )}
              {i < previewURLs.length - 1 && (
                <button
                  type="button"
                  onClick={() => onMoveImage(i, i + 1)}
                  className="p-2 bg-white rounded-full hover:bg-gray-100"
                  title={labels.moveRight || "Move right"}
                >
                  <ChevronRight className="w-4 h-4 text-gray-900" />
                </button>
              )}
              {i !== coverIndex && (
                <button
                  type="button"
                  onClick={() => onSetCover(i)}
                  className="p-2 bg-white rounded-full hover:bg-gray-100"
                  title={labels.setAsCover || "Set as cover"}
                >
                  <Star className="w-4 h-4 text-gray-900" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onRemoveImage(i)}
                className="p-2 bg-red-500 rounded-full hover:bg-red-600"
                title={labels.remove || "Remove"}
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {previewURLs.length < maxImages && (
        <button
          type="button"
          onClick={handleFileSelect}
          disabled={uploading}
          className="w-full py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-900/50 transition disabled:opacity-50"
        >
          {labels.addMorePhotos || "Add More Photos"}
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => onAddImages(Array.from(e.target.files || []))}
        disabled={uploading || previewURLs.length >= maxImages}
        className="hidden"
      />

      {uploading && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {labels.uploading || "Uploading"} {uploadProgress.done}/{uploadProgress.total}...
          </p>
          <div className="mt-2 h-1 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all"
              style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}